import { ApiResult, HttpClient } from "../http.js";
import {
  CalculationPhase,
  CalculationPositionDto,
  CalculationSettingsDto,
  CalculationStatus,
  ProjectCalculationDto,
} from "../types/project-calculations.js";

/**
 * Position/sub-item payloads reuse the read DTO shapes verbatim — the API
 * persists client-computed totals (parity contract), so what you send is what
 * you read back.
 */
export type CalculationPositionPayload = CalculationPositionDto;

export interface CreateProjectCalculationPayload {
  projectId?: string | null;
  quoteId?: string | null;
  title: string;
  description?: string | null;
  status: CalculationStatus;
  calculationPhase?: CalculationPhase | null;
  settings: CalculationSettingsDto;
  positions?: CalculationPositionPayload[] | null;
  grandTotalHours?: number;
  grandTotalLabor?: number;
  grandTotalMaterials?: number;
  grandTotalEquipment?: number;
  grandTotalSubcontractor?: number;
  grandTotalCost?: number;
  grandTotalWithProfit?: number;
  generatedQuoteId?: string | null;
  parentCalculationId?: string | null;
  templateId?: string | null;
  templateVersion?: string | null;
  id?: string;
}

export interface UpdateProjectCalculationPayload {
  title: string;
  description?: string | null;
  status: CalculationStatus;
  settings: CalculationSettingsDto;
  positions?: CalculationPositionPayload[] | null;
  grandTotalHours?: number;
  grandTotalLabor?: number;
  grandTotalMaterials?: number;
  grandTotalEquipment?: number;
  grandTotalSubcontractor?: number;
  grandTotalCost?: number;
  grandTotalWithProfit?: number;
  generatedQuoteId?: string | null;
}

export interface ListProjectCalculationsQuery {
  projectId?: string;
  quoteId?: string;
  phase?: CalculationPhase;
}

export class ProjectCalculationsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListProjectCalculationsQuery = {}): Promise<ApiResult<ProjectCalculationDto[]>> {
    return this.http.get<ProjectCalculationDto[]>("/api/project-calculations", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<ProjectCalculationDto>> {
    return this.http.get<ProjectCalculationDto>(`/api/project-calculations/${encodeURIComponent(id)}`);
  }

  create(payload: CreateProjectCalculationPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/project-calculations", { body: payload });
  }

  update(
    id: string,
    payload: UpdateProjectCalculationPayload,
  ): Promise<ApiResult<ProjectCalculationDto>> {
    return this.http.put<ProjectCalculationDto>(
      `/api/project-calculations/${encodeURIComponent(id)}`,
      { body: payload },
    );
  }

  /** Lock a calculation against further edits. */
  lock(id: string): Promise<ApiResult<ProjectCalculationDto>> {
    return this.http.post<ProjectCalculationDto>(
      `/api/project-calculations/${encodeURIComponent(id)}/lock`,
    );
  }

  /** Clone a quote-phase calculation into a new startup-phase calculation. */
  createStartup(quoteCalculationId: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/project-calculations/${encodeURIComponent(quoteCalculationId)}/startup`,
    );
  }

  /** Clone a startup-phase calculation into a new tracking-phase calculation. */
  createTracking(startupCalculationId: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/project-calculations/${encodeURIComponent(startupCalculationId)}/tracking`,
    );
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/project-calculations/${encodeURIComponent(id)}`);
  }
}
